import config from "./config";
import { getBeHeaders } from "./utils/apiHeaders";
import { getAuthorizedFetch } from './utils/fetchRegistry';

/**
 * ADK client for managing agent communication
 *
 * Encapsulates HTTP requests to the ADK backend for session management and
 * streaming message exchanges. Keeps transport details out of UI components.
 */
class AdkClientService {
    constructor(config, appName = "hub") {
        this.config = config;
        this._fallbackAppName = appName;
        this.USER_ID = null;
        // Track which sessions have already received full file context
        this.sessionsWithFiles = new Set();
        this.overrideAppName = null;
    }

    /**
     * The app name used when no playbook has set an override. A getter, not a
     * value captured at construction time, because config.defaultAppName is
     * only populated once loadHubConfig()'s fetch resolves — which happens
     * after this singleton is constructed at module-init time (see the
     * bottom of this file).
     */
    get APP_NAME() {
        return this.config.defaultAppName || this._fallbackAppName;
    }

    /**
     * Set the authenticated user ID.
     * Must be called once the user has logged in so that session and message
     * requests are associated with the real user identity instead of a placeholder.
     * Pass null to clear the user ID on logout.
     * @param {string|null} userId - The authenticated user's identifier (sub, id, or email)
     */
    setUserId(userId) {
        this.USER_ID = userId || null;
    }

    /**
     * Get the current user ID.
     * Throws if no user ID has been set yet (i.e. no user is logged in).
     * @returns {string}
     */
    getUserId() {
        if (!this.USER_ID) {
            throw new Error('AdkClient: no authenticated user ID set. Call setUserId() after login.');
        }
        return this.USER_ID;
    }

    /**
     * Set an override for the app name (used for custom agents)
     * @param {string|null} name - The app name to use, or null to use default
     */
    setOverrideAppName(name) {
        this.overrideAppName = name;
    }

    /**
     * Mark a session as already having received files.
     * Call this when reusing an existing session to prevent re-sending files.
     * @param {string} sessionId - Session ID to mark
     */
    markSessionInitialized(sessionId) {
        this.sessionsWithFiles.add(sessionId);
    }

    /**
     * Clear the file tracking for a session, forcing files to be re-sent on next message.
     * Use this when files have been modified and the AI needs fresh context.
     * @param {string} sessionId - Session ID to clear
     */
    clearSessionFiles(sessionId) {
        this.sessionsWithFiles.delete(sessionId);
    }

    /**
     * Create a new session with the ADK backend.
     *
     * Contract
     * - POST {agentUrl}/apps/{appName}/users/{userId}/sessions with an empty body.
     * - Expects { id: string } in the JSON response.
     *
     * @returns {Promise<{success: boolean, sessionId?: string, message?: string}>}
     */
    async createSession() {
        try {
            const currentAppName = this.overrideAppName || this.APP_NAME;
            const response = await getAuthorizedFetch()(
                `${this.config.agentUrl}/apps/${currentAppName}/users/${this.getUserId()}/sessions`,
                {
                    method: 'POST',
                    headers: getBeHeaders({
                        'Content-Type': 'application/json',
                    }),
                    body: JSON.stringify({}),
                }
            );
            
            if (!response.ok) {
                return {success: false, message: 'Failed to create session'};
            }
            
            const data = await response.json();
            return {success: true, sessionId: data.id};
        } catch (error) {
            return {success: false, message: `Error creating session: ${error.message}`};
        }
    }

    /**
     * Send a message to the ADK agent and obtain a streaming Server-Sent Events (SSE) response.
     *
     * Behavior
     * - Posts a JSON payload to {agentUrl}/run_sse with Accept: text/event-stream.
     * - On 2xx, returns the raw Response object; caller is responsible for
     *   reading Response.body as a stream (ReadableStream of Uint8Array chunks)
     *   and decoding/parsing SSE frames.
     * - On non-2xx, returns a failure with the response text for diagnostics.
     *
     * @param {string} sessionId - Existing session identifier from createSession()
     * @param {string} message - User message to send (plain text)
     * @param {AbortSignal} [signal] - Optional AbortSignal for canceling the request
     * @param {Object} [projectContext] - Optional project context (tenant, project, revision) for canvas mode
     * @param {boolean} [isCanvasMode] - Whether the user is in canvas mode (determines agent selection)
     * @param {string} [aiMode] - AI mode: 'agent' (can create things) or 'plan' (information only)
     * @param {Array} [attachedFiles] - Optional array of file attachments (images and PDFs)
     * @param {Array} [selectedTools] - Optional array of selected tool names for this session
     * @param {string} selectedModel - Optional model override for this message
     * @param {Object} [customAgentConfig] - Optional custom agent configuration { name: string, data: object }
     * @param {string} [tenantId] - Active tenant ID (for catalog agent resolution on the backend)
     * @returns {Promise<{success: boolean, response?: Response, message?: string}>}
     */
    async sendMessage(sessionId, message, signal = null, projectContext = null, isCanvasMode = false, aiMode = 'agent', attachedFiles = null, selectedTools = null, selectedModel = null, customAgentConfig = null, tenantId = null) {
        try {
            const payload = {
                appName: this.overrideAppName || this.APP_NAME,
                userId: this.getUserId(),
                sessionId: sessionId,
                newMessage: {role: 'user', parts: [{text: message}]},
                streaming: true,
                aiMode: aiMode,
                stateDelta: {
                    "override_model": selectedModel,
                },
                customAgentData: customAgentConfig?.data
            };

            if (tenantId) {
                payload.tenantId = tenantId;
            }
            
            // Add selected tools if provided
            if (selectedTools !== null) {
                payload.selectedTools = selectedTools;
            }
            
            // Add file attachments if provided
            if (attachedFiles && attachedFiles.length > 0) {
                const mediaIds = [];
                
                // Upload each file and get media_id
                for (const file of attachedFiles) {
                    try {
                        const formData = new FormData();
                        // Create a Blob from base64 content
                        const byteCharacters = atob(file.content);
                        const byteNumbers = new Array(byteCharacters.length);
                        for (let i = 0; i < byteCharacters.length; i++) {
                            byteNumbers[i] = byteCharacters.charCodeAt(i);
                        }
                        const byteArray = new Uint8Array(byteNumbers);
                        const blob = new Blob([byteArray], { type: file.type });
                        
                        formData.append('file', blob, file.name);
                        
                        // Upload file to backend with session ID
                        const uploadUrl = `${this.config.agentUrl}/upload-media/${sessionId}`;
                        const response = await getAuthorizedFetch()(uploadUrl, {
                            method: 'POST',
                            headers: getBeHeaders(),
                            body: formData,
                        });
                        
                        if (!response.ok) {
                            throw new Error(`Upload failed: ${response.statusText}`);
                        }
                        
                        const result = await response.json();
                        mediaIds.push(result.media_id);
                    } catch (error) {
                        console.error(`❌ Failed to upload ${file.name}:`, error);
                        throw error; // Re-throw to stop sending message
                    }
                }
                
                // Store mediaIds in projectContext
                if (!payload.projectContext) {
                    payload.projectContext = {};
                }
                payload.projectContext.mediaIds = mediaIds;
            }
            
            // Add project context if provided (for canvas mode)
            if (projectContext) {
                // Check if this is the first message in the session
                const isFirstMessage = !this.sessionsWithFiles.has(sessionId);
                
                // Send METADATA ONLY (no content) to save tokens - AI requests content on-demand via tools
                let fileMetadata = null;
                if (isFirstMessage && projectContext.allFiles) {
                    // Filter out binary files, then strip content to send only metadata
                    fileMetadata = projectContext.allFiles
                        .filter(file => {
                            const ext = file.path.split('.').pop().toLowerCase();
                            
                            // Binary/Asset files to EXCLUDE
                            const binaryExtensions = [
                                'png', 'jpg', 'jpeg', 'gif', 'bmp', 'svg', 'ico', 'webp', 'avif',
                                'woff', 'woff2', 'ttf', 'otf', 'eot',
                                'mp4', 'mov', 'avi', 'mkv', 'webm', 'mp3', 'wav', 'ogg', 'flac',
                                'zip', 'tar', 'gz', 'rar', '7z', 'bz2',
                                'exe', 'dll', 'so', 'dylib', 'bin', 'dmg', 'pkg',
                                'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'
                            ];
                            
                            if (binaryExtensions.includes(ext)) {
                                return false;
                            }
                            
                            // Source code and config files to INCLUDE
                            const codeExtensions = [
                                'js', 'jsx', 'ts', 'tsx', 'mjs', 'cjs',
                                'html', 'css', 'scss', 'sass', 'less',
                                'py', 'pyw', 'pyx', 'pyi',
                                'java', 'kt', 'kts', 'scala',
                                'c', 'cpp', 'cc', 'cxx', 'h', 'hpp', 'hxx',
                                'go', 'rs', 'rb', 'php', 'swift', 'cs', 'sh', 'bash', 'zsh',
                                'json', 'yaml', 'yml', 'toml', 'xml', 'ini', 'conf', 'config',
                                'md', 'mdx', 'txt', 'rst',
                                'gradle', 'maven', 'cmake', 'make', 'dockerfile'
                            ];
                            
                            return codeExtensions.includes(ext);
                        })
                        .map(file => ({
                            path: file.path,
                            lines: file.content ? file.content.split('\n').length : 0,
                            extension: file.path.split('.').pop()
                            // NOTE: No content field - AI will request it via request_canvas_file_content tool
                        }));
                    
                    // Mark this session as having received files
                    this.sessionsWithFiles.add(sessionId);
                }
                
                // Preserve mediaIds if they were set earlier
                const existingMediaIds = payload.projectContext?.mediaIds;
                
                // Only send file metadata on first message, not the full project context
                // AI can request full context via request_project_context tool
                payload.projectContext = {
                    // Preserve mediaIds from uploaded files
                    ...(existingMediaIds && { mediaIds: existingMediaIds }),
                    // Send file METADATA only (no content) - AI requests content via tools on-demand
                    allFiles: fileMetadata
                    // NOTE: Full project context (tenant, project, revision, etc.) is NOT sent by default
                    // AI will request it via request_project_context tool when needed
                };
            }
            
            const fetchOptions = {
                method: 'POST',
                headers: getBeHeaders({
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream',
                }),
                body: JSON.stringify(payload),
            };
            
            // Add signal if provided
            if (signal) {
                fetchOptions.signal = signal;
            }
            
            const response = await getAuthorizedFetch()(
                `${this.config.agentUrl}/run_sse`,
                fetchOptions
            );

            if (!response.ok) {
                const errorText = await response.text();
                let errorMessage = `Server error (${response.status})`;
                
                // Try to parse error response for more details
                try {
                    const errorJson = JSON.parse(errorText);
                    if (errorJson.error) {
                        errorMessage = errorJson.error;
                        if (errorJson.detail) {
                            errorMessage += `: ${errorJson.detail}`;
                        }
                    }
                } catch (e) {
                    // If not JSON, use the raw error text
                    if (errorText) {
                        errorMessage = errorText;
                    }
                }
                
                return {success: false, message: `Error: ${errorMessage}`};
            }

            return {success: true, response: response};
        } catch (error) {
            // Network errors, timeouts, or other fetch failures
            let errorMessage = 'Network error: Unable to reach the server';
            
            if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                errorMessage = 'Network error: Server is not responding. Please check if the backend is running.';
            } else if (error.name === 'AbortError') {
                errorMessage = 'Request timeout: The server took too long to respond.';
            } else if (error.message) {
                errorMessage = `Connection error: ${error.message}`;
            }
            
            return {success: false, message: errorMessage};
        }
    }
}

// Export singleton instance for convenience
const AdkClient = new AdkClientService(config);

export default AdkClient;
